import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Drawer,
  IconButton, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { usePersonnelDocumentStatus } from "@/api/hooks/participations";
import { useLocationPersonnelHistory } from "@/api/hooks/locations";
import { DocumentDetailDrawer } from "@/components/documents/DocumentDetailDrawer";
import { DocumentUploadDrawer } from "@/components/documents/DocumentUploadDrawer";
import type {
  DocumentType, PersonnelDocumentStatus,
} from "@/api/types";

const STATUS_DOT: Record<PersonnelDocumentStatus["status"], string> = {
  current: "#16a34a",
  missing: "#dc2626",
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Roster row being viewed. Drives both the doc-status fetch (by id)
   * and the role-history fetch (by name on this location). */
  personnel: {
    id: string;
    first_name: string | null;
    last_name: string;
    role_label: string | null;
  } | null;
  /** Optional — when present, the role-history table renders alongside
   * the doc-status table. Without it, doc status renders solo. */
  locationId?: string | null;
}

/**
 * Per-person modal showing required-document compliance + (when a
 * locationId is known) the role-change history for that person at
 * this location. Click-target for any name in the cohort roster.
 */
export function PersonnelDetailDialog({
  open, onClose, personnel, locationId,
}: Props) {
  const docStatus = usePersonnelDocumentStatus(
    personnel?.id ?? null,
    { enabled: open },
  );
  const history = useLocationPersonnelHistory(
    locationId ?? undefined,
    personnel?.first_name ?? "",
    personnel?.last_name ?? "",
    { enabled: open && Boolean(locationId) },
  );

  // Doc upload / view drawers for the per-doc row buttons. URL params
  // give us the site + DA so we can invalidate the right query keys
  // after a successful upload.
  const { id: siteId, da_code: daCode } = useParams<{
    id: string; da_code?: string;
  }>();
  const [uploadFor, setUploadFor] = useState<DocumentType | null>(null);
  const [viewDocId, setViewDocId] = useState<string | null>(null);

  const displayName = personnel
    ? (personnel.first_name
      ? `${personnel.first_name} ${personnel.last_name}`
      : personnel.last_name)
    : "Personnel";

  const docRows = docStatus.data?.rows ?? [];
  const historyRows = history.data ?? [];

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{ paper: { sx: { width: { xs: "100%", md: 720 } } } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1,
          px: 2, py: 1.5, borderBottom: 1, borderColor: "divider",
        }}>
          <Box sx={{ flex: 1, fontWeight: 600 }}>
            {displayName}
            {personnel?.role_label && (
              <Chip
                size="small"
                label={personnel.role_label}
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
              >
                Required documents
              </Typography>
              {docStatus.isLoading && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={18} />
                </Box>
              )}
              {docStatus.error && (
                <Alert severity="error">
                  Could not load document status:{" "}
                  {docStatus.error instanceof Error
                    ? docStatus.error.message
                    : String(docStatus.error)}
                </Alert>
              )}
              {!docStatus.isLoading && !docStatus.error && docRows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No documents required for this role / task combination.
                </Typography>
              )}
              {docRows.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Document</TableCell>
                      <TableCell>Why required</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {docRows.map((r) => (
                      <TableRow key={r.document_type} hover>
                        <TableCell>{r.label}</TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              r.source === "task"
                                ? `Triggered by delegated task: ${r.source_detail}`
                                : `Required for the ${r.source_detail.toUpperCase()} role`
                            }
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={
                                r.source === "task"
                                  ? `task · ${r.source_detail}`
                                  : `role · ${r.source_detail}`
                              }
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                            <Box
                              sx={{
                                width: 9, height: 9, borderRadius: "50%",
                                bgcolor: STATUS_DOT[r.status],
                              }}
                            />
                            {r.status === "current" ? "Current" : "Missing"}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {r.document_id ? (
                            <Button
                              size="small"
                              onClick={() => setViewDocId(r.document_id)}
                            >
                              View / replace
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              onClick={() =>
                                setUploadFor(r.document_type as DocumentType)
                              }
                            >
                              Upload
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            {locationId && (
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
                >
                  Role / title history at this location
                </Typography>
                {history.isLoading && (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
                {history.error && (
                  <Alert severity="error">
                    Could not load history:{" "}
                    {history.error instanceof Error
                      ? history.error.message
                      : String(history.error)}
                  </Alert>
                )}
                {!history.isLoading && !history.error && historyRows.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No history rows found for this person at this location.
                  </Typography>
                )}
                {historyRows.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Recorded</TableCell>
                        <TableCell>Role / Title</TableCell>
                        <TableCell>Study</TableCell>
                        <TableCell>Delegated tasks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyRows.map((p, i) => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                            {new Date(p.created_at).toLocaleDateString()}
                            {i === 0 && (
                              <Chip size="small" label="Latest" color="success" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{p.role_label}</TableCell>
                          <TableCell>
                            <Tooltip title={p.study_name}>
                              <Chip size="small" label={p.study_code} variant="outlined" />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {p.delegated_tasks.length === 0
                              ? "—"
                              : p.delegated_tasks.join(", ")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}
          </Stack>
        </Box>
        <Box sx={{
          display: "flex", justifyContent: "flex-end",
          px: 2, py: 1.5, borderTop: 1, borderColor: "divider",
        }}>
          <Button onClick={onClose}>Close</Button>
        </Box>
        </Box>
      </Drawer>

      <DocumentUploadDrawer
        open={uploadFor !== null}
        onClose={() => setUploadFor(null)}
        entityType="location_personnel"
        entityId={personnel?.id ?? ""}
        documentType={uploadFor ?? undefined}
        invalidateKeys={[
          ["location-personnel", personnel?.id ?? "", "document-status"],
          ["sites", siteId, "clinops", daCode],
          ["documents"],
        ]}
      />
      <DocumentDetailDrawer
        open={viewDocId !== null}
        onClose={() => setViewDocId(null)}
        documentId={viewDocId}
        invalidateKeys={[
          ["location-personnel", personnel?.id ?? "", "document-status"],
          ["sites", siteId, "clinops", daCode],
          ["documents"],
        ]}
      />
    </>
  );
}
